import { useRef } from 'react';
import { FlipHorizontal, FlipVertical, Image as ImageIcon, X } from 'lucide-react';
import {
  calculateEffectiveDpi,
  dpiStatus,
  getProductProfile,
  type PhotoFrameElement,
} from '@fotoboek/domain';
import { useProjectStore } from '../../../state/projectStore';
import { importPhotoFiles } from '../../../photos/importPhotos';
import { Label, Input } from '../../ui/Input';
import { t } from '../../../i18n';
import './photo-properties.css';

export function PhotoProperties({
  element,
  onChange,
}: {
  element: PhotoFrameElement;
  onChange: (patch: Partial<PhotoFrameElement>) => void;
}) {
  const project = useProjectStore((s) => s.project);
  const assetsRuntime = useProjectStore((s) => s.assetsRuntime);
  const addAssets = useProjectStore((s) => s.addAssets);
  const setElementAssetId = useProjectStore((s) => s.setElementAssetId);
  const selection = useProjectStore((s) => s.selection);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const asset = element.assetId ? assetsRuntime[element.assetId] : undefined;

  const dpiInfo = (() => {
    if (!asset || !project) return null;
    const profile = getProductProfile(project.productProfileId);
    const dpi = calculateEffectiveDpi({
      sourceWidthPx: asset.widthPx,
      sourceHeightPx: asset.heightPx,
      frameWidthMm: element.width,
      frameHeightMm: element.height,
      placementScale: element.placement.scale,
    });
    return { dpi: Math.round(dpi), status: dpiStatus(dpi, profile) };
  })();

  const handleReplace = async (file: File) => {
    if (!project || !selection.target) return;
    const result = await importPhotoFiles(project.id, [file]);
    const imported = result.imported[0];
    if (!imported) return;
    addAssets([imported.asset], {
      [imported.asset.id]: {
        objectUrl: imported.objectUrl,
        thumbnailUrl: imported.thumbnailUrl,
        widthPx: imported.asset.widthPx,
        heightPx: imported.asset.heightPx,
      },
    });
    setElementAssetId(selection.target, element.id, imported.asset.id);
  };

  return (
    <div className="properties-section">
      <div className="left-panel-section-title">{t('editor.properties.photo.heading')}</div>

      <div className="photo-properties-preview">
        {asset ? <img src={asset.thumbnailUrl} alt="" /> : <ImageIcon size={24} />}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
        className="visually-hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void handleReplace(file);
          e.target.value = '';
        }}
      />
      <div className="photo-properties-actions">
        <button className="left-panel-action-btn" onClick={() => fileInputRef.current?.click()}>
          {t('editor.properties.photo.replace')}
        </button>
        {element.assetId ? (
          <button
            className="left-panel-action-btn"
            onClick={() => setElementAssetId(selection.target!, element.id, null)}
          >
            <X size={14} />
            {t('editor.properties.photo.remove')}
          </button>
        ) : null}
      </div>

      {dpiInfo ? (
        <p className={`photo-dpi-status ${dpiInfo.status}`}>
          {dpiInfo.status === 'ok'
            ? t('editor.properties.photo.dpiOk')
            : dpiInfo.status === 'warn'
              ? t('editor.properties.photo.dpiWarn')
              : t('editor.properties.photo.dpiLow')}{' '}
          (~{dpiInfo.dpi} dpi)
        </p>
      ) : null}

      {element.assetId ? (
        <>
          <div className="field-group">
            <Label>{t('editor.properties.photo.zoom')}</Label>
            <input
              type="range"
              min={1}
              max={3}
              step={0.01}
              value={element.placement.scale}
              onChange={(e) =>
                onChange({ placement: { ...element.placement, scale: Number(e.target.value) } })
              }
            />
          </div>
          <div className="properties-row">
            <button
              className={element.placement.flipX ? 'photo-flip-btn active' : 'photo-flip-btn'}
              onClick={() =>
                onChange({ placement: { ...element.placement, flipX: !element.placement.flipX } })
              }
              title={t('editor.properties.photo.flipH')}
            >
              <FlipHorizontal size={16} />
            </button>
            <button
              className={element.placement.flipY ? 'photo-flip-btn active' : 'photo-flip-btn'}
              onClick={() =>
                onChange({ placement: { ...element.placement, flipY: !element.placement.flipY } })
              }
              title={t('editor.properties.photo.flipV')}
            >
              <FlipVertical size={16} />
            </button>
          </div>
        </>
      ) : null}

      <div className="field-group">
        <Label>{t('editor.properties.photo.cornerRadius')}</Label>
        <Input
          type="number"
          min={0}
          value={Math.round(element.cornerRadiusMm)}
          onChange={(e) => onChange({ cornerRadiusMm: Math.max(0, Number(e.target.value)) })}
        />
      </div>

      <div className="left-panel-section-title">{t('editor.properties.photo.filters')}</div>
      {(['brightness', 'contrast', 'saturation', 'warmth'] as const).map((key) => (
        <div className="field-group" key={key}>
          <Label>{t(`editor.properties.photo.${key}`)}</Label>
          <input
            type="range"
            min={-1}
            max={1}
            step={0.05}
            value={element.filters[key]}
            onChange={(e) =>
              onChange({ filters: { ...element.filters, [key]: Number(e.target.value) } })
            }
          />
        </div>
      ))}
      <label className="photo-grayscale-toggle">
        <input
          type="checkbox"
          checked={element.filters.grayscale}
          onChange={(e) =>
            onChange({ filters: { ...element.filters, grayscale: e.target.checked } })
          }
        />
        {t('editor.properties.photo.grayscale')}
      </label>
    </div>
  );
}
